import {deepClone, GoingNewUtils, StorageUtils} from 'xmgoing-utils';
import {QueryCommand} from "xmgoing-server";
import GoingUtils from "@/utils/goingUtils";
import {getParamsRelVal, initQueryParamsObj, resolveExportDataConfig} from "@/main-page/utils/domainQueryUtils";
import {appendParamsRelToExportParams} from "@/main-page/utils/gridQuerySyncUtils";
import {downloadByData} from "@/utils/file/download";

/** 表格导出交付结果：文件流下载 / 异步任务已提交 / 失败 */
export type TableExportResult = 'file' | 'async' | false;

const parseExportBodyJson = (data: ArrayBuffer) => {
  try {
    const text = new TextDecoder('utf-8').decode(data);
    const json = JSON.parse(text);
    return json && typeof json === 'object' ? json : null;
  } catch {
    return null;
  }
};

const hasExportCode = (code: unknown) => code != null && code !== '';

const isExportSuccessCode = (code: unknown) => Number(code) === 200 || code == 200;

const resolveExportDownloadName = (response: any, fileName: string) => {
  const disposition = response?.headers?.['content-disposition'] || '';
  let headerName = disposition.match(/filename=([^;]+)/)?.[1];
  if (headerName) {
    try {
      headerName = decodeURI(headerName);
    } catch {
      // keep raw
    }
  }
  return headerName || fileName;
};

const downloadExportFile = (response: any, fileName: string) => {
  const contentType =
    response?.headers?.['content-type'] ||
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  let mime = contentType;
  if (typeof mime === 'string' && mime.indexOf('charset=') === -1) {
    mime = [mime, 'charset=UTF-8'].join(';');
  }
  downloadByData(response.data, resolveExportDownloadName(response, fileName), mime);
};

/**
 * 执行表格导出：始终接 callback，按回包判定交付（不看 asyncOutImport）。
 * 'file' 已触发本地下载；'async' 需提示到操作记录；false 为失败（错误提示由 executeExport 处理）。
 */
const runTableExport = function ({url, fileName, params}): Promise<TableExportResult> {
  const queryCommand = new QueryCommand({});
  let settled = false;
  return new Promise((resolve) => {
    const finish = (result: TableExportResult) => {
      if (!settled) {
        settled = true;
        resolve(result);
      }
    };
    Promise.resolve(
      queryCommand.executeExport(url, fileName, params, (response) => {
        if (!(response?.status >= 200 && response?.status < 300)) {
          finish(false);
          return;
        }
        const json = parseExportBodyJson(response.data);
        const code = json?.code;
        if (hasExportCode(code)) {
          finish(isExportSuccessCode(code) ? 'async' : false);
          return;
        }
        downloadExportFile(response, fileName);
        finish('file');
      })
    ).then(() => {
      // 业务错误时可能不进回调，统一视为失败（错误提示由 executeExport 内部处理）
      finish(false);
    }).catch(() => finish(false));
  });
};

/**
 * 获取明细信息根据版本信息
 * @param pageId
 * @param backFun
 */
export function loadPageDesDetail(pageId, backFun) {
  if(pageId){
    //如果存在缓存 则通过缓存加载
    const formDetailStr = StorageUtils.getSessionItem(pageId + "_FormDetail");
    if (formDetailStr && formDetailStr.length > 0) {
      return backFun(JSON.parse(formDetailStr));
    }
    const queryCommand = new QueryCommand({url: '/api/go-lcc/menuInfoSettingAct/detail', method: "GET"});
    queryCommand.executeQueryPromise({params: {id: pageId}}).then((res) => {
      backFun(res);
      StorageUtils.addSessionItem(pageId + "_FormDetail", JSON.stringify(res))
    })
  }
}

/**
 * 加载表单的权限信息
 * @param pageId
 * @param backFun
 */
export function _loadFormFieldPerm(params, backFun) {
  const queryCommand = new QueryCommand({url: '/api/xm-flow/node/fieldPerm/detail', method: "GET"});
  queryCommand.executeQueryPromise({params}).then((res) => {
    backFun(res);
  })
}


/**
 * 保存表单数据
 * @param dataState
 */
export function saveFormData({formData, mainTableObj, backFun, errorFun}) {
  if (unref(formData) && unref(formData).id) {
    const queryCommand = new QueryCommand({url: `/api/go-lcc/execute/updateByTableCode/${mainTableObj.tableCode}`});
    queryCommand.executeQueryPromise({params: {...formData}}).then((res) => {
      backFun && backFun(res);
    }).catch((e) => {
      GoingUtils.showErrorMessage(e.msg)
      errorFun && errorFun();
    })
  } else {
    const queryCommand = new QueryCommand({url: `/api/go-lcc/execute/saveByTableCode/${mainTableObj.tableCode}`});
    queryCommand.executeQueryPromise({params: {...formData}}).then((res) => {
      backFun && backFun(res);
    }).catch((e) => {
      GoingUtils.showErrorMessage(e.msg)
      errorFun && errorFun();
    })
  }

}



/**
 * 查询枚举信息集合
 * @param menuId
 * @param backFun
 * @param errorFun
 */
export function queryEnumList({appId, backFun, errorFun}: any) {
  const queryCommand = new QueryCommand({url: `/api/pe-portal/enumInfo/list`, method: "GET", showTip: false});
  queryCommand.executeQueryPromise({params: {appId}}).then((res) => {
    backFun && backFun(res);
  }).catch(() => {
    errorFun && errorFun();
  })
}

/**
 * 查询所有枚举
 * @param appId
 * @param backFun
 * @param errorFun
 */
export function queryAllEnumList({appId, backFun, errorFun}: any) {
  const queryCommand = new QueryCommand({url: `/api/pe-portal/enumInfo/listAll`, method: "GET", showTip: false});
  queryCommand.executeQueryPromise({params: {}}).then((res) => {
    backFun && backFun(res);
  }).catch(() => {
    errorFun && errorFun();
  })
}

/**
 * 查询枚举明细信息
 * @param enumId
 * @param backFun
 * @param errorFun
 */
export function queryEnumDetail({enumId, backFun, errorFun}: any) {
  const queryCommand = new QueryCommand({url: `/api/pe-portal/enumVal/list`, method: "GET", showTip: false});
  queryCommand.executeQueryPromise({
    params: {
      enumInfoId: enumId,
      current: 1,
      size: GoingNewUtils.maxPageSize,
    }
  }).then((res) => {
    backFun && backFun(res);
  }).catch(() => {
    errorFun && errorFun();
  })
}

export function queryEnumDetailByCode({enumCode, backFun, errorFun}: any) {
  const queryCommand = new QueryCommand({url: `/api/pe-portal/enum/detailByname`, method: "POST", showTip: false});
  queryCommand.executeQueryPromise({params: {name: enumCode}}).then((res) => {
    backFun && backFun(res);
  }).catch(() => {
    errorFun && errorFun();
  })
}


export function queryListByUrl({url, backFun, errorFun}: any) {
  if(url==null){
    return
  }
  const queryCommand = new QueryCommand({url, method: "POST", showTip: false});
  queryCommand.executeQueryPromise({params: {}}).then((res) => {
    backFun && backFun(res);
  }).catch(() => {
    errorFun && errorFun();
  })
}

export function queryDomainDetailByCode({tableCode, backFun, errorFun}: any) {
  const queryCommand = new QueryCommand({url: `/api/go-lcc/metatable/listColumnByTableCode`, method: "GET"});
  queryCommand.executeQueryPromise({params: {tableCode}}).then((res) => {
    backFun && backFun(res);
  }).catch(() => {
    errorFun && errorFun();
  })
}

/**
 * 根据界面编码获取可导出（配置）的组件列/字段列表
 * @param pageCode 主表界面 pageCode（主子界面开启时填“关联界面code”，否则为当前界面自身 pageCode）
 * @param mainKey 主表组件key
 * @param subKey 子表组件key
 * @param bjModuleCode 部件模块编码
 * @param bjPageCode 部件界面编码
 */
export function getComponentByPageCode({pageCode, mainKey, subKey, bjModuleCode, bjPageCode, backFun, errorFun}: any) {
  const params: any = {pageCode};
  if (mainKey) params.mainKey = mainKey;
  if (subKey) params.subKey = subKey;
  if (bjModuleCode) params.bjModuleCode = bjModuleCode;
  if (bjPageCode) params.bjPageCode = bjPageCode;
  const queryCommand = new QueryCommand({url: `/api/go-lcc/menuInfoSettingAct/getComponentByPageCode`, method: "GET"});
  queryCommand.executeQueryPromise({params}).then((res) => {
    backFun && backFun(res);
  }).catch((e) => {
    errorFun && errorFun(e);
  })
}

/**
 * 与「导出所有」对齐的列表侧查询入参（查询缓存、合并后的 queryUserParams、表格过滤、查询范围）
 */
const buildExportListSideParams = function ({gridObj, dataConfig, gridKey, gridTableStore, pageModelsStore}) {
  const cache = gridTableStore.getQueryCache(gridKey) || {};
  Object.keys(cache).forEach((key) => {
    const val = cache[key];
    // 更精确的判断：只删除 null、undefined、空字符串、空数组
    if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
      delete cache[key];
    }
  })
  const exportDataConfig = resolveExportDataConfig(dataConfig, gridObj);
  let queryParamsDs: any = deepClone(exportDataConfig.queryUserParams);
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  if (tableObj.filterQueryParams && tableObj.filterQueryParams.length > 0) {
    if (queryParamsDs && Array.isArray(queryParamsDs)) {
      queryParamsDs = [...queryParamsDs, ...tableObj.filterQueryParams];
    } else {
      queryParamsDs = [...tableObj.filterQueryParams];
    }
  }
  const orderParams = {};
  if (queryParamsDs && Array.isArray(queryParamsDs)) {
    queryParamsDs.forEach((itemDb) => {
      initQueryParamsObj(itemDb, pageModelsStore, orderParams);
    });
  }
  const params: any = {...cache, ...(orderParams || {})};
  const exportScopeType = unref(dataConfig)?.lccQueryScopeType;
  if (exportScopeType && exportScopeType !== 'currentAccount') {
    params.lccQueryScopeType = exportScopeType;
  }
  return params;
}

/**
 * 根据附件id集合 导出数据
 * @param gridObj
 */
export const exportByIds = function ({gridObj, dataConfig, gridKey, gridTableStore, props, pageModelsStore, extraParams = {}}) {
  try {
    const ids: any = [];
    if (gridTableStore.getGridSels(gridKey) && gridTableStore.getGridSels(gridKey).length > 0) {
      gridTableStore.getGridSels(gridKey).forEach((record) => {
        ids.push(record || record.id || record.key);
      })
    }
    let params: any;
    if (unref(props)?.selOutWithListQuery === true) {
      const listSideParams = buildExportListSideParams({
        gridObj, dataConfig, gridKey, gridTableStore, pageModelsStore,
      });
      params = {id_in: ids.join(";"), ...listSideParams, ...extraParams};
    } else {
      const orderParams = {};
      const queryParamsDs = unref(dataConfig).queryUserParams;
      if (queryParamsDs && Array.isArray(queryParamsDs)) {
        queryParamsDs.forEach((itemDb) => {
          initQueryParamsObj(itemDb, pageModelsStore, orderParams);
        })
      }
      params = {id_in: ids.join(";"), ...(orderParams || {}), ...extraParams};
    }
    delete params.paginationSize;
    delete params.size;
    delete params.current;
    appendParamsRelToExportParams(params, gridTableStore, gridKey, dataConfig);
    return runTableExport({
      url: unref(dataConfig).useQueryUrl || props.userDefUrl || `/api/go-lcc/execute/exportByTableCode/${unref(dataConfig).domainCode || gridObj.tableCode}`,
      fileName: (unref(props).fileName || gridObj.moduleName) + ".xlsx",
      params,
    });
  } catch (error) {
    console.log(error);
  }
}


/**
 * 导出所有数据
 * @param gridObj
 */
export const exportAll = function ({gridObj, dataConfig, gridKey, gridTableStore, props, pageModelsStore, extraParams = {}}) {
  const listSideParams = buildExportListSideParams({
    gridObj, dataConfig, gridKey, gridTableStore, pageModelsStore,
  });
  const params: any = {...listSideParams, ...extraParams};

  delete params.paginationSize;
  delete params.size;
  delete params.current;
  appendParamsRelToExportParams(params, gridTableStore, gridKey, dataConfig);
  try {
    return runTableExport({
      url: unref(dataConfig).useQueryUrl || unref(props).userDefUrl || `/api/go-lcc/execute/exportByTableCode/${unref(dataConfig).domainCode || gridObj.tableCode}`,
      fileName: (unref(props).fileName || gridObj.moduleName) + ".xlsx",
      params,
    });
  } catch (error) {
    console.log(error);
  }
}
